import React, { useState, useEffect } from "react";
import {
  Form,
  notification,
  Select,
  Upload,
  Radio,
  Input,
  Modal,
  Button,
} from "antd";
import { useNavigate } from "react-router-dom";
import { useDispatch } from "react-redux";
import { InboxOutlined } from "@ant-design/icons";
import { cfbhAxios, directoryAxios } from "utilities/axios";
import { fetchApplications } from "utilities/redux/slices/applicationsSlice";

const NewApplication = ({ visible, setVisible }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [form] = Form.useForm();
  const allValues = Form.useWatch([], form);

  const [clinics, setClinics] = useState([]);
  const [selectedClinic, setSelectedClinic] = useState(null);

  useEffect(() => {
    directoryAxios
      .get("/clinics")
      .then((response) => {
        setClinics(response.data.clinics);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: `Failed to fetch clinics: ${error.message}`,
        });
      });
  }, []);

  useEffect(() => {
    directoryAxios
      .get(`/clinics/${allValues?.clinic}`)
      .then((response) => {
        setSelectedClinic(response.data);
      })
      .catch((error) => {
        notification.error({
          message: "Error",
          description: `Failed to fetch clinic details: ${error.message}`,
        });
      });
  }, [allValues?.clinic]);

  const handleSubmit = () => {
    form.validateFields().then((values) => {
      const formData = new FormData();
      if (values.clinic) formData.append("clinicNo", values.clinic);
      if (values.client_name) formData.append("clientName", values.client_name);
      if (values.hp) formData.append("hpInitials", values.hp);
      if (values.csa) formData.append("csaInitials", values.csa);
      if (values.nomination_reason) formData.append("nominationReason", values.nomination_reason);
      if (values.aids_count) formData.append("hearingAidCount", values.aids_count);
      if (values.brand) formData.append("hearingAidMake", values.brand);
      if (values.model) formData.append("hearingAidModel", values.model);
      if (values.invoice_total) formData.append("invoiceTotal", values.invoice_total);

      if (
        values.audiogram &&
        Array.isArray(values.audiogram) &&
        values.audiogram.length > 0 &&
        values.audiogram[0].originFileObj
      ) {
        formData.append("audiogram", values.audiogram[0].originFileObj);
      }

      cfbhAxios
        .post("/applications", formData)
        .then((res) => {
          notification.success({
            message: "Success",
            description: "Application submitted successfully",
          });
          setVisible(false);
          form.resetFields();
          navigate(`/applications/${res.data}`);
          dispatch(fetchApplications({ page: 1 }));
        })
        .catch((error) => {
          notification.error({
            message: "Error",
            description: `Failed to submit application: ${error.message}`,
          });
        });
    });
  };

  return (
    <Modal
      title="New Nomination"
      open={visible}
      onCancel={() => setVisible(false)}
      onClose={() => setVisible(false)}
      footer={[
        <Button key="cancel" onClick={() => setVisible(false)}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" onClick={handleSubmit}>
          Submit Nomination
        </Button>,
      ]}
      styles={{
        body: {
          overflow: "auto",
          maxHeight: "70vh",
          padding: "0 16px",
        },
      }}
      width="50vw"
      destroyOnClose
    >
      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Form.Item
          name="clinic"
          label="Select Clinic"
          rules={[{ required: true, message: "Please select a clinic" }]}
        >
          <Select placeholder="Select a clinic">
            {clinics?.map((clinic) => (
              <Select.Option key={clinic?.clinic} value={clinic?.clinic}>
                {clinic?.clinicName}
              </Select.Option>
            ))}
          </Select>
        </Form.Item>
        <Form.Item
          name="client_name"
          label="Client Name"
          rules={[
            { required: true, message: "Please enter the client's name" },
          ]}
        >
          <Input placeholder="Enter client name" />
        </Form.Item>
        <Form.Item
          name="hp"
          label="Hearing Practitioner"
          rules={[
            {
              required: true,
              message: "Please select the Hearing Practitioner",
            },
          ]}
        >
          <Select placeholder="Select a Hearing Practitioner">
            {selectedClinic?.employees && Array.isArray(selectedClinic.employees)
              ? selectedClinic.employees
                  .filter(
                    (employee) => employee.functionalTitle === "HP"
                  )
                  .map((hp) => (
                    <Select.Option key={hp.initials} value={hp.initials}>
                      {hp.name} ({hp.initials})
                    </Select.Option>
                  ))
              : null}
          </Select>
        </Form.Item>
        <Form.Item name="csa" label="CSA (Optional)">
          <Select placeholder="Select a CSA">
            {selectedClinic?.employees && Array.isArray(selectedClinic.employees)
              ? selectedClinic.employees
                  .filter(
                    (employee) => employee.functionalTitle === "CSA"
                  )
                  .map((csa) => (
                    <Select.Option key={csa.initials} value={csa.initials}>
                      {csa.name} ({csa.initials})
                    </Select.Option>
                  ))
              : null}
          </Select>
        </Form.Item>
        <Form.Item
          name="nomination_reason"
          label="Your Reason for Nomination"
          rules={[
            {
              required: true,
              message: "Please enter your reason for nomination",
            },
          ]}
        >
          <Input.TextArea rows={4} placeholder="Enter nomination reason" />
        </Form.Item>
        <Form.Item
          name="audiogram"
          label="Upload Audiogram"
          valuePropName="fileList"
          getValueFromEvent={e => Array.isArray(e) ? e : e && e.fileList}
          rules={[{ required: true, message: "Please upload an audiogram" }]}
        >
          <Upload.Dragger
            name="audiogram"
            beforeUpload={() => false}
            customRequest={({ onSuccess }) => setTimeout(() => onSuccess("ok"), 0)}
          >
            <p className="ant-upload-drag-icon">
              <InboxOutlined />
            </p>
            <p className="ant-upload-text">
              Click or drag file to this area to upload
            </p>
          </Upload.Dragger>
        </Form.Item>
        <Form.Item
          name="aids_count"
          label="Number of Hearing Aids"
          rules={[
            {
              required: true,
              message: "Please enter the number of hearing aids",
            },
          ]}
        >
          <Radio.Group>
            <Radio value={1}>1</Radio>
            <Radio value={2}>2</Radio>
          </Radio.Group>
        </Form.Item>
        <Form.Item
          name="brand"
          label="Hearing Aid Brand"
          rules={[
            { required: true, message: "Please enter the hearing aid brand" },
          ]}
        >
          <Input placeholder="Enter hearing aid brand" />
        </Form.Item>
        <Form.Item
          name="model"
          label="Hearing Aid Model"
          rules={[
            { required: true, message: "Please enter the hearing aid model" },
          ]}
        >
          <Input placeholder="Enter hearing aid model" />
        </Form.Item>
        <Form.Item
          name="invoice_total"
          label="Invoice Total"
          rules={[
            { required: true, message: "Please enter the invoice total" },
          ]}
        >
          <Input type="number" placeholder="Enter invoice total" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default NewApplication;
